import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import dynamic from 'next/dynamic';
import { useSignIn, SignInVariables } from '@Hooks/Api/useSignIn';
import { SignInProps } from './SignIn';
import { useRouter } from '@Helpers/useRouter';
import { QueryParams, RoutingPaths } from '@App/paths';
import { useNotificationsContext } from '@Contexts';
import { Routes } from '@App/paths';
import { setFieldErrors } from '@Hooks/useSetFieldErrors';
import { useCreateMatchmakingApplyFundNonProfitLink } from '@Hooks/Api/useCreateMatchmakingApplyFundNonProfitLink';

/** TODO: Workaround for class name hydration issue between server and client */
const SignInNoSSR = dynamic<SignInProps>(
  () => import('./SignIn').then((mod) => mod.SignIn) as any,
  {
    ssr: false,
  },
);

export const SignInController = () => {
  const { t } = useTranslation('createAccount');
  const methods = useForm();
  const { register, handleSubmit, errors, setError } = methods;
  const { push, params } = useRouter();
  const { signIn, response } = useSignIn();
  const { error, isError, loading } = response;
  const { error: errorNotification } = useNotificationsContext();
  const applyFundId = params[QueryParams.APPLY_FUND_ID]
    ? parseInt(params[QueryParams.APPLY_FUND_ID] as string)
    : undefined;
  const [createMatchmakingApplyFundNonProfitLink] = useCreateMatchmakingApplyFundNonProfitLink();

  const onSubmit = useCallback(
    (data: SignInVariables) => {
      signIn(data)
        .then((response) => {
          // Only redirect from here if the user is not two-factor authenticated
          if (response.data && !response.data.two_factor_required) {
            if (applyFundId) {
              createMatchmakingApplyFundNonProfitLink(applyFundId).then(() => {
                push(Routes.PROJECT_CREATE);
              });
            } else {
              push(RoutingPaths.DASHBOARD);
            }
          }
        })
        .catch((error) => {
          errorNotification(error.data.detail[0]);
        });
    },
    [signIn, applyFundId, createMatchmakingApplyFundNonProfitLink, push, errorNotification],
  );

  useEffect(() => {
    if (isError) {
      setError('email', { message: t('errors.invalid_login') });
    }
    if (error?.field_errors && !loading) {
      setFieldErrors(
        error?.field_errors,
        // We're not expecting any non-field errors here
        () => {},
        loading,
      );
    }
  }, [isError, error, setError, loading, t]);

  return (
    <FormProvider {...methods}>
      <SignInNoSSR
        onSubmit={handleSubmit(onSubmit)}
        register={register}
        fieldErrors={errors}
        isLoading={loading}
      />
    </FormProvider>
  );
};
